// Quick-schedule presets + date/time formatting helpers shared by the
// send-button popover and the "Schedule message" modal.

export interface SchedulePreset {
  readonly id: string;
  readonly label: string;
  /** Resolve the preset to an absolute Date at call time. */
  readonly compute: () => Date;
}

function plusMinutes(mins: number): Date {
  const d = new Date();
  d.setMinutes(d.getMinutes() + mins, 0, 0);
  return d;
}

function tomorrowAt9(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

function nextMondayAt9(): Date {
  const d = new Date();
  const day = d.getDay(); // 0 Sun … 6 Sat
  const daysUntilMonday = (8 - day) % 7 || 7; // strictly the next Monday
  d.setDate(d.getDate() + daysUntilMonday);
  d.setHours(9, 0, 0, 0);
  return d;
}

/** Preset chips, matching the Schedule-message design. */
export const SCHEDULE_PRESETS: readonly SchedulePreset[] = [
  { id: "30m", label: "In 30 min", compute: () => plusMinutes(30) },
  { id: "1h", label: "In 1 hour", compute: () => plusMinutes(60) },
  { id: "4h", label: "In 4 hours", compute: () => plusMinutes(240) },
  { id: "tomorrow", label: "Tomorrow 9:00 AM", compute: tomorrowAt9 },
  { id: "monday", label: "Monday 9:00 AM", compute: nextMondayAt9 },
];

/** "Saturday, Jul 25, 10:48 AM" — the modal's live send-time preview. */
export function formatScheduleSummary(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/** "Jul 25, 10:48 AM" — compact form for the popover / pill. */
export function formatScheduleShort(d: Date): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

// ── <input type="datetime-local"> value <-> Date (LOCAL time) ──────────
// The control's value is `YYYY-MM-DDTHH:mm` in the user's local zone; the
// naive `Date.toISOString()` would shift by the UTC offset, so build the
// string from local getters.
function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function toDatetimeLocalValue(d: Date): string {
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function fromDatetimeLocalValue(value: string): Date | null {
  // `YYYY-MM-DDTHH:mm` parsed as LOCAL time.
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (m === null) return null;
  const [, y, mo, da, h, mi] = m;
  const d = new Date(
    Number(y),
    Number(mo) - 1,
    Number(da),
    Number(h),
    Number(mi),
    0,
    0,
  );
  return Number.isNaN(d.getTime()) ? null : d;
}
